import React, { useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { PressableWithIntent } from '@zet/common/components/pressableWithIntent'
import { B5, Icon, XStack, colors as zetColors } from '@zet/design-system'
import { i18n } from '@zet/localization'
import { LinearGradient } from 'expo-linear-gradient'

import { useBrandDetailsById } from '../../../api/brands/hooks'
import { creditCardCrossSellingData } from '../../../api/crossSelling/types'
import Stars from './../../../assets/svg/stars.svg'
import CrossSellingBottomSheet from './crossSellingBottomSheet'

interface CreditCardCrossSellingBannerProps {
  crossSellingData: creditCardCrossSellingData
  customerName: string
  customerId: number
  customerPhoneNumber: number
  tier: string
  extraPayoutPercentage: number
}

const CreditCardCrossSellingBanner = ({
  crossSellingData,
  customerName,
  customerId,
  customerPhoneNumber,
  tier,
  extraPayoutPercentage,
}: CreditCardCrossSellingBannerProps) => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const partnerId = crossSellingData?.partner?.partnerId
  const { data: brandContent } = useBrandDetailsById(partnerId || '')
  const crossSellingBrandName = brandContent?.name
  const crossSellingBrandCategory = brandContent?.subCategory

  const onBannerClick = () => {
    setIsBottomSheetVisible(true)
    publishEvent('click_action', {
      item_name: 'cross-nonapi-widget',
      brand_name: crossSellingBrandName,
      brand_category: crossSellingBrandCategory,
    })
  }

  useEffect(() => {
    if (crossSellingData?.partner) {
      publishEvent('widget_view', { widget_name: 'credit_card_cross_selling' })
    }
  }, [crossSellingData])

  return (
    <LinearGradient
      colors={['#171C24', '#465163']}
      start={[0, 0]}
      end={[1, 0.0]}
      locations={[0.0, 1.0]}
      className="pb-3 pt-1"
      style={styles.roundedBorder}
    >
      <PressableWithIntent
        name="credit-card-cross-selling-banner"
        onPress={onBannerClick}
        className="m-2"
      >
        <XStack className="bg-red items-center gap-2">
          <Stars />
          {i18n.locale === 'en' ? (
            <B5 className="text-neutral-white100 w-4/5">
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {customerName}
              </B5>
              {i18n.t('creditCardCrossSelling.hasOver')}
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {crossSellingData?.partner?.approvalRate}
              </B5>
              {i18n.t('creditCardCrossSelling.chanceOfApproval')}
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {crossSellingBrandName}
              </B5>
            </B5>
          ) : (
            <B5 className="text-neutral-white100 w-4/5">
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {customerName}
              </B5>
              {i18n.t('creditCardCrossSelling.hasOver')}
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {crossSellingBrandName}
              </B5>
              {i18n.t('creditCardCrossSelling.chanceOfApproval')}
              <B5 className="text-neutral-white100 font-[Graphie-Bold]">
                {crossSellingData?.partner?.approvalRate}
              </B5>
              {i18n.t('creditCardCrossSelling.moreThan')}
            </B5>
          )}
          <Icon type="ArrowRight" color={zetColors.neutral.white100} />
        </XStack>
      </PressableWithIntent>
      {isBottomSheetVisible && (
        <CrossSellingBottomSheet
          isBottomSheetVisible={isBottomSheetVisible}
          setIsBottomSheetVisible={setIsBottomSheetVisible}
          crossSellingData={crossSellingData}
          customerName={customerName}
          customerId={customerId}
          customerPhoneNumber={customerPhoneNumber}
          crossSellingBrandName={crossSellingBrandName}
          tier={tier}
          extraPayoutPercentage={extraPayoutPercentage}
        />
      )}
    </LinearGradient>
  )
}

export default CreditCardCrossSellingBanner

const styles = StyleSheet.create({
  roundedBorder: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
})
